import React, { useMemo, useState } from "react";
import {
  AssignmentOutlined,
  ArrowDownward,
  ArrowUpward,
  EventOutlined,
  GroupsOutlined,
  HourglassEmptyOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";
import { customSort, formatDate, formatDuration, getTranslation, SortCriterion } from "common/utilities";
import { useTranslations } from "hooks";
import { SortState } from "hooks/useSortableList";
import { SupportedUserDataTypes } from "hooks/useUserDataKeys";
import { TaskDetail } from "./TaskItem";

interface ProcessItemCollapseProps {
  expanded: boolean;
  tasks: TaskDetail[];
}

type CollapseSortKey = "activity" | "startedAt" | "timetotakeover" | "timeindoing" | "participant";

const ProcessItemCollapse: React.FC<ProcessItemCollapseProps> = ({ expanded, tasks }) => {
  const translations = useTranslations();
  const [sort, setSort] = useState<SortCriterion>({});

  const handleChangeSort = (key: CollapseSortKey) => {
    setSort((current) => {
      switch (current[key]) {
        case undefined:
        case SortState.None:
          return { [key]: SortState.Desc };
        case SortState.Asc:
          return { [key]: SortState.None };
        case SortState.Desc:
          return { [key]: SortState.Asc };
      }
    });
  };

  const getActivityStartDate = (task: TaskDetail) => task.startdoing || task.start_date;

  const sortedTasks = useMemo(() => {
    const activeSortKey = Object.keys(sort).find((key) => sort[key] !== undefined && sort[key] !== SortState.None) as CollapseSortKey | undefined;

    if (!activeSortKey) {
      return tasks;
    }

    const direction = sort[activeSortKey] === SortState.Asc ? 1 : -1;

    return [...tasks].sort((a, b) => {
      let result = 0;

      switch (activeSortKey) {
        case "activity":
          result = customSort(a.flownodename || "", b.flownodename || "", SupportedUserDataTypes.string);
          break;
        case "startedAt": {
          const dateA = getActivityStartDate(a);
          const dateB = getActivityStartDate(b);
          result = customSort(
            dateA ? dayjs(dateA).format("DD-MM-YYYY HH:mm") : "",
            dateB ? dayjs(dateB).format("DD-MM-YYYY HH:mm") : "",
            SupportedUserDataTypes.date
          );
          break;
        }
        case "timetotakeover":
          result = customSort(a.timetotakeover ?? 0, b.timetotakeover ?? 0, SupportedUserDataTypes.number);
          break;
        case "timeindoing":
          result = customSort(a.timeindoing ?? 0, b.timeindoing ?? 0, SupportedUserDataTypes.number);
          break;
        case "participant":
          result = customSort(a.userfullname || "", b.userfullname || "", SupportedUserDataTypes.string);
          break;
      }

      return direction * result;
    });
  }, [sort, tasks]);

  const renderCollapseLabel = (icon: React.ReactNode, label: string, variant: "subtitle2" | "body2" = "body2") => (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        component="span"
        sx={{
          alignItems: "center",
          color: "primary.main",
          display: "inline-flex",
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        {icon}
      </Box>
      <Typography component="span" variant={variant} sx={{ fontWeight: "bold" }}>
        {label}
      </Typography>
    </Box>
  );

  const renderSortableCollapseLabel = (sortKey: CollapseSortKey, icon: React.ReactNode, label: string) => (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      {renderCollapseLabel(icon, label)}
      <IconButton
        sx={{ opacity: sort[sortKey] ? 1 : 0.3, mt: "0px !important" }}
        size="small"
        onClick={() => handleChangeSort(sortKey)}
      >
        {sort[sortKey] === SortState.Asc ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
      </IconButton>
    </Box>
  );

  return (
    <TableRow>
      <TableCell colSpan={12} sx={{ p: 0, borderBottom: "none" }}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              backgroundColor: "rgba(84, 189, 193, 0.05)",
              border: "1px solid rgba(84, 189, 193, 0.16)",
              borderRadius: 2,
              m: 1,
              p: 2,
            }}
          >
            <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: "0 6px" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ borderBottom: "none", color: "secondary.main", fontWeight: "bold", pb: 0.5 }}>
                    {renderSortableCollapseLabel("activity", <AssignmentOutlined fontSize="small" />, getTranslation(translations, "generic.task"))}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", color: "secondary.main", fontWeight: "bold", pb: 0.5 }}>
                    {renderSortableCollapseLabel("startedAt", <EventOutlined fontSize="small" />, "Started at")}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", color: "secondary.main", fontWeight: "bold", pb: 0.5 }}>
                    {renderSortableCollapseLabel("timetotakeover", <HourglassEmptyOutlined fontSize="small" />, getTranslation(translations, "generic.waiting_time.caption"))}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", color: "secondary.main", fontWeight: "bold", pb: 0.5 }}>
                    {renderSortableCollapseLabel("timeindoing", <TimerOutlined fontSize="small" />, getTranslation(translations, "generic.working_time.caption"))}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "none", color: "secondary.main", fontWeight: "bold", pb: 0.5 }}>
                    {renderSortableCollapseLabel("participant", <GroupsOutlined fontSize="small" />, getTranslation(translations, "generic.participants"))}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTasks.map((task) => {
                  const activityStartDate = getActivityStartDate(task);

                  return (
                    <TableRow
                      key={task.idprocesstoken}
                      sx={{
                        "&:hover .MuiTableCell-root": {
                          backgroundColor: "rgba(84, 189, 193, 0.08)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          backgroundColor: "#fff",
                          borderBottom: "none",
                          borderBottomLeftRadius: 8,
                          borderTopLeftRadius: 8,
                          color: "secondary.main",
                          py: 1.25,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1 }}>
                          <Typography component="span" variant="body2" sx={{ fontWeight: "bold" }}>
                            {task.flownodename}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#fff", borderBottom: "none", color: "secondary.main", py: 1.25 }}>
                        {activityStartDate ? formatDate(activityStartDate) : "N/A"}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#fff", borderBottom: "none", color: "secondary.main", py: 1.25 }}>
                        {task.timetotakeover
                          ? formatDuration(task.timetotakeover)
                          : "N/A"}
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#fff", borderBottom: "none", color: "secondary.main", py: 1.25 }}>
                        {task.timeindoing
                          ? formatDuration(task.timeindoing)
                          : "N/A"}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#fff",
                          borderBottom: "none",
                          borderBottomRightRadius: 8,
                          borderTopRightRadius: 8,
                          color: "secondary.main",
                          py: 1.25,
                        }}
                      >
                        {task.userfullname || "N/A"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  );
};

export default ProcessItemCollapse;
